import React, { AnimationEventHandler, FC, Fragment, useMemo } from "react";
import { FlatListItem } from "../../../components/partials/FlatList";
import { Result } from "../../../models/result.model";
import { Test } from "../../../models/test.model";
import { markToDouble } from "../../../tools";
import { AnimatedFlatList } from "./components/AnimatedFlatList";
import { Flag } from "./components/Flag";
import { usePagination } from "./hooks/usePagination";
import { SquareList } from "./components/SquareList";
import { PaginatedArea } from "./components/AnimatedArea";
import { SponsorLogo } from "./components/SponsorLogo";
import {
  SecondaryNameColumn,
  RankColumn,
  ResultColumn,
  PrimaryNameColumn,
  ScopeColumn,
  TeamColumn,
  Column,
} from "./components/Column";
import { useIsBreeding } from "./hooks/useIsBreeding";

type Phase = "PREL" | "AFIN" | "BFIN" | "CFIN" | "FIN";

const LIGHT_BACKGROUNDS = ["white", "yellow"];

const ResultListItem: React.FC<
  FlatListItem<Result, { phase: Phase; test: Test }>
> = ({ item: result, show, onHidden, onShown, extraData }) => {
  const animationEnd: AnimationEventHandler<HTMLLIElement> = (event) => {
    if (event.animationName === "scaleOut") {
      onHidden?.();
    }

    if (event.animationName === "scaleIn") {
      onShown?.();
    }
  };

  const textColor = LIGHT_BACKGROUNDS.includes(result.color)
    ? "black"
    : "white";

  return (
    <li
      className={`flatlist-item ${show ? "show" : "hide"}`}
      style={{ gridTemplateColumns: "1fr 1rem" }}
      onAnimationEnd={animationEnd}
    >
      <div
        className="row-content"
        style={{
          gridTemplateColumns: "1fr 1fr 5ch 4ch",
        }}
      >
        <div
          className="row-marker"
          style={{
            backgroundColor:
              extraData.phase !== "PREL" && extraData.phase !== "FIN"
                ? `var(--${result.color})`
                : "var(--blue)",
            color: textColor,
          }}
        >
          {result.rank}
        </div>
        <div>{result.rider?.fullname}</div>
        <div>{result.horse?.horseName}</div>
        <div className="text-right">
          <b>
            {markToDouble(result.mark, extraData.test?.roundingPrecision ?? 2)}
          </b>
        </div>
        <Flag
          countryCode={result.rider?.team ?? ""}
          style={{
            marginLeft: "auto",
            height: "1em",
          }}
        />
      </div>
      {result.scope && (
        <div className={`row-end-marker ${result.scope.toLowerCase()}`}>
          {result.scope[0]}
        </div>
      )}
    </li>
  );
};

type ResultListProps = {
  results: Result[];
  test: Test;
  phase: string;
};

const ResultListClassic: React.FC<ResultListProps> = ({
  results,
  test,
  phase,
}) => {
  return (
    <AnimatedFlatList
      header={
        <>
          {test.testName} {phase} - <small>Results</small>
        </>
      }
      headerImg={test.sponsorLogo ?? "assets/img/ICeCompass_Logo_Final6.png"}
      items={results}
      RenderComponent={ResultListItem}
      itemsPerPage={10}
      extraData={{ phase, test }}
      timePerPage={15000}
    />
  );
};

const ResultListSquare: React.FC<ResultListProps> = ({
  results,
  test,
  phase,
}) => {
  const { page, nextPage, itemsPerPage } = usePagination(results);
  console.log(results);

  const hasFlag = page.some(
    (item) => item?.rider?.team && item.rider?.team !== "",
  );

  const cols = page.map((item, index) =>
    SquareResultListTableRow({
      item: item,
      test: test,
      phase: phase,
      isLast: index === page.length - 1,
      ...{ hasFlag },
    }),
  );

  const gridColumns = cols[0][1];

  return (
    <SquareList style={{ width: "80vw" }}>
      <header>
        <h1>
          {test.testName} {phase} - <small>Results</small>
        </h1>
      </header>
      <PaginatedArea
        resetKey={test.testName}
        enableAnimation={results.length > itemsPerPage}
        onHidden={nextPage}
      >
        <div
          className="bs-table"
          style={{
            gridTemplateColumns: gridColumns,
          }}
        >
          {cols.map(([col, _, id]) => (
            <Fragment key={id}>{col}</Fragment>
          ))}
        </div>
        <SponsorLogo src={test.sponsorLogo} />
      </PaginatedArea>
    </SquareList>
  );
};

const SquareResultListTableRow = ({
  item,
  test,
  isLast,
  phase,
  hasFlag,
}: {
  item: Result;
  test: Test;
  phase?: string;
  isLast: boolean;
  hasFlag?: boolean;
}) => {
  const isBreeding = useIsBreeding(test);

  const gridColumns = ["minmax(6ch, max-content)"];

  const columns = [<RankColumn result={item} phase={phase} isLast={isLast} />];

  if (hasFlag) {
    columns.push(<TeamColumn rider={item.rider} isLast={isLast} />);
    gridColumns.push("max-content");
  }

  columns.push(<PrimaryNameColumn test={test} entry={item} isLast={isLast} />);
  gridColumns.push("1fr");

  if (!isBreeding) {
    columns.push(
      <SecondaryNameColumn test={test} entry={item} isLast={isLast} />,
    );
    gridColumns.push("1fr");
  }

  if (isBreeding) {
    const sectionMarks =
      test.sections
        ?.map((section) => {
          return {
            section,
            marks:
              item.marks?.flatMap(
                (mark) =>
                  mark.sectionMarks?.filter(
                    (sm) => sm.sectionNo === section.sectionNo,
                  ) ?? [],
              ) ?? [],
          };
        })
        .filter((item) => [1, 11].includes(item.section.sectionNo)) ?? [];

    const sections = sectionMarks.flatMap((sm) => {
      return sm.marks.flatMap((mark) => (
        <Column key={`${mark.id}${sm.section.sectionNo}`} isLast={isLast}>
          {sm.section.sectionName
            .split(" ")
            .map((w) => w[0])
            .join("")}
          : {markToDouble(mark.mark, test.roundingPrecision ?? 2)}
        </Column>
      ));
    });

    columns.push(...sections);
    gridColumns.push(`repeat(${sections.length}, min-content)`);
  }

  columns.push(<ResultColumn result={item} test={test} isLast={isLast} />);
  gridColumns.push("max-content");

  if (!isBreeding) {
    columns.push(<ScopeColumn scope={item.scope} isLast={isLast} />);
    gridColumns.push("4ch");
  }

  return [columns, gridColumns.join(" "), item.id] as const;
};

export const ResultList: React.FC<
  ResultListProps & { variant: "classic" | "square" }
> = ({ variant, phase, ...rest }) => {
  const phaseText = useMemo(() => {
    switch (phase) {
      case "PREL":
        return "Preliminary";
      case "AFIN":
        return "A-final";
      case "BFIN":
        return "B-final";
      case "CFIN":
        return "C-final";
      default:
        return "";
    }
  }, [phase]);

  switch (variant) {
    case "classic":
      return <ResultListClassic phase={phaseText} {...rest} />;
    case "square":
      return <ResultListSquare phase={phaseText} {...rest} />;
    default:
      return null;
  }
};
